/**
 * Single source of truth for "are we running inside the Capacitor native
 * shell, or in a plain browser tab?". Every later piece of code that branches
 * on this (push, storage, geolocation plugin) goes through these helpers.
 *
 * Implementation note: we DON'T import from @capacitor/core at module top
 * level, because in the PWA-only build (no Capacitor installed) that import
 * would explode at build time. Instead we look at the global Capacitor object
 * that the native shell injects into the WebView before any JS runs. On the
 * PWA, the global is undefined and these helpers degrade to safe defaults.
 */

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?:      () => 'web' | 'ios' | 'android';
}

function capacitor(): CapacitorGlobal | undefined {
  return (globalThis as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export function isNativePlatform(): boolean {
  const c = capacitor();
  return typeof c?.isNativePlatform === 'function' ? c.isNativePlatform() : false;
}

export function getPlatform(): 'web' | 'ios' | 'android' {
  const c = capacitor();
  if (typeof c?.getPlatform === 'function') return c.getPlatform();
  return 'web';
}

export const isAndroid = () => getPlatform() === 'android';
export const isIOS     = () => getPlatform() === 'ios';
